import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import { SentimentBadge, IntentScore, StatusBadge, PersonaBadge } from "@/components/Badges";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Rocket, X, ExternalLink, ArrowLeft, Clock, User, Hash, Edit3 } from "lucide-react";
import {
  ReactFlow, Background, Controls, Handle, Position, MiniMap,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const PERSONAS = ["Agitator", "Advocate", "Closer", "Standardizer"];
const ACTIONS = ["Comment", "Upvote", "Downvote", "Reply", "Post Image"];

function TaskNode({ data }) {
  const t = data.task;
  const agent = data.agentMap[t.assigned_agent_id];
  return (
    <div
      onClick={() => data.onClick(t)}
      data-testid={`task-node-${t.id}`}
      className="w-[240px] bg-[#09090B] border border-white/15 hover:border-[#FF3B30]/60 transition-colors cursor-pointer p-3"
    >
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center justify-between mb-2">
        <PersonaBadge persona={t.persona_angle} />
        <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1">
          <Clock className="w-3 h-3" /> +{t.execution_delay_minutes}m
        </div>
      </div>
      <div className="text-[12px] font-medium text-white mb-1">{t.action_type}</div>
      <div className="text-[11px] text-zinc-400 line-clamp-3 min-h-[3em]">
        {t.task_script || <em className="text-zinc-600">no script — action-only</em>}
      </div>
      <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between">
        <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1 truncate">
          <User className="w-3 h-3" /> {agent?.name || "?"}
        </div>
        <StatusBadge status={t.status} />
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const nodeTypes = { taskNode: TaskNode };

export default function ThreadDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const q = useQuery({
    queryKey: ["thread", id],
    queryFn: () => api.get(`/threads/${id}`).then(r => r.data),
    refetchInterval: 5000,
  });

  const thread = q.data?.thread;
  const campaign = q.data?.campaign;
  const tasks = q.data?.tasks || [];

  const campaignFull = useQuery({
    queryKey: ["campaign-full", campaign?.id],
    enabled: !!campaign?.id,
    queryFn: () => api.get(`/campaigns/${campaign.id}`).then(r => r.data),
    refetchInterval: 5000,
  });
  const agents = campaignFull.data?.agents || [];
  const agentMap = useMemo(() => Object.fromEntries(agents.map(a => [a.id, a])), [agents]);
  const liveTasks = campaignFull.data?.tasks || tasks;

  const orchestrate = useMutation({
    mutationFn: () => api.post(`/threads/${id}/orchestrate`, {}),
    onSuccess: (r) => {
      const st = r?.data?.orchestration_status;
      if (st === "generating") {
        toast.success("Orchestration started — Jarvis is composing your campaign in the background. Tasks will appear here as they're ready.");
      } else {
        toast.success("Campaign blueprint generated");
      }
      qc.invalidateQueries({queryKey:["thread",id]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Orchestration failed"),
  });

  const deploy = useMutation({
    mutationFn: () => api.post(`/campaigns/${campaign.id}/deploy`),
    onSuccess: () => { toast.success("Campaign deployed to agents"); qc.invalidateQueries({queryKey:["campaign-full",campaign.id]}); qc.invalidateQueries({queryKey:["thread",id]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Deploy failed"),
  });

  const overrideSub = useMutation({
    mutationFn: (subreddit) => api.patch(`/campaigns/${campaign.id}/subreddit`, { subreddit }),
    onSuccess: () => { toast.success("Subreddit updated"); qc.invalidateQueries({queryKey:["thread",id]}); qc.invalidateQueries({queryKey:["campaign-full",campaign.id]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const savedSubsQ = useQuery({
    queryKey: ["subreddits"],
    queryFn: () => api.get("/subreddits").then(r => r.data),
    enabled: !!campaign,
  });
  const [showSubPicker, setShowSubPicker] = useState(false);

  const updateTask = useMutation({
    mutationFn: (payload) => api.patch(`/tasks/${payload.id}`, payload.body),
    onSuccess: () => { toast.success("Task updated"); qc.invalidateQueries({queryKey:["campaign-full",campaign.id]}); setEditing(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const { nodes, edges } = useMemo(() => {
    if (!liveTasks?.length) return { nodes: [], edges: [] };
    // Build a randomized branching layout instead of a strict linear timeline.
    // Group by persona rows so it feels like a graph, not a list.
    const rowOrder = ["Agitator", "Advocate", "Closer", "Standardizer"];
    const rowIndex = Object.fromEntries(rowOrder.map((p, i) => [p, i]));
    const perRow = {};
    liveTasks.forEach((t) => {
      const p = t.persona_angle;
      perRow[p] = perRow[p] || [];
      perRow[p].push(t);
    });
    const nodes = [];
    Object.entries(perRow).forEach(([persona, arr]) => {
      arr.sort((a,b) => a.execution_delay_minutes - b.execution_delay_minutes);
      arr.forEach((t, i) => {
        nodes.push({
          id: t.id,
          type: "taskNode",
          position: { x: 60 + i * 300, y: 40 + (rowIndex[persona] ?? 0) * 210 },
          data: { task: t, agentMap, onClick: setEditing },
        });
      });
    });
    // Chain edges by chronological delay to hint at sequence
    const sorted = [...liveTasks].sort((a,b) => a.execution_delay_minutes - b.execution_delay_minutes);
    const edges = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      edges.push({
        id: `e${i}`,
        source: sorted[i].id, target: sorted[i+1].id,
        animated: false, style: { stroke: "rgba(255,255,255,0.12)" },
      });
    }
    return { nodes, edges };
  }, [liveTasks, agentMap]);

  if (q.isLoading) return <Layout title="Thread"><div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div></Layout>;
  if (!thread) return <Layout title="Thread"><div className="text-zinc-500 text-sm">Not found.</div></Layout>;

  return (
    <Layout
      subtitle={<span><Link to="/admin" className="hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Back to queue</Link></span>}
      title="Campaign Orchestrator."
      right={
        <div className="flex items-center gap-3">
          {!campaign && (
            <button
              onClick={() => orchestrate.mutate()}
              disabled={orchestrate.isPending}
              data-testid="orchestrate-btn"
              className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            >
              {orchestrate.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
              Generate Campaign
            </button>
          )}
          {campaign && !campaign.deployed_at && (
            <button
              onClick={() => setShowSubPicker(true)}
              data-testid="change-subreddit-btn"
              className="border border-amber-500/40 hover:border-amber-500 text-amber-400 font-medium px-3 py-2 flex items-center gap-2 text-[13px]"
            >
              <Hash className="w-4 h-4"/>
              Change subreddit
            </button>
          )}
          {campaign && !campaign.deployed_at && (
            <button
              onClick={() => deploy.mutate()}
              disabled={deploy.isPending}
              data-testid="approve-deploy-btn"
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            >
              {deploy.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Rocket className="w-4 h-4"/>}
              Approve &amp; Deploy
            </button>
          )}
          {campaign?.deployed_at && (
            <div className="border border-emerald-500/40 text-emerald-400 px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Deployed</div>
          )}
        </div>
      }
    >
      {/* Thread summary */}
      <div className="border border-white/10 p-5 mb-6 bg-[#09090B]">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="font-mono text-[11px] text-[var(--accent)]">{thread.subreddit}</span>
          <span className="text-zinc-600">·</span>
          <span className="font-mono text-[11px] text-zinc-500">u/{thread.author}</span>
          <span className="text-zinc-600">·</span>
          <IntentScore score={thread.commercial_intent_score} />
          <SentimentBadge value={thread.syften_sentiment} />
          <StatusBadge status={thread.status} />
          <a href={thread.item_url} target="_blank" rel="noreferrer" className="ml-auto text-zinc-400 hover:text-white text-[12px] flex items-center gap-1" data-testid="thread-external-link">
            Open in Reddit <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
        <h2 className="font-display font-black text-2xl tracking-tight mb-2">{thread.title}</h2>
        <p className="text-[13px] text-zinc-400 leading-relaxed whitespace-pre-wrap">{thread.ingested_text}</p>
        {thread.intent_reason && (
          <div className="mt-3 pt-3 border-t border-white/5 font-mono text-[11px] text-zinc-500">
            <span className="text-zinc-600">// intent_reason:</span> {thread.intent_reason}
          </div>
        )}
      </div>

      {/* Campaign summary */}
      {campaign && (
        <div className="border border-white/10 px-5 py-4 mb-4 flex items-center gap-6 bg-[#0a0a0c]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Objective</div>
            <div className="text-[14px] text-zinc-100">{campaign.objective}</div>
          </div>
          <div className="ml-auto flex items-center gap-8">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Agents</div>
              <div className="font-mono text-lg text-white">{campaign.total_agents_assigned}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tasks</div>
              <div className="font-mono text-lg text-white">{liveTasks.length}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Status</div>
              <div><StatusBadge status={campaign.admin_approval_status} /></div>
            </div>
          </div>
        </div>
      )}

      {/* Node graph */}
      {campaign && (
        <div className="border border-white/10 h-[560px] bg-[#050505] relative bg-dots">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            proOptions={{ hideAttribution: true }}
            nodesDraggable
          >
            <Background gap={24} size={1} color="rgba(255,255,255,0.06)" />
            <Controls position="bottom-right" showInteractive={false} />
          </ReactFlow>
        </div>
      )}

      {!campaign && (
        <div className="border border-dashed border-white/15 p-16 text-center bg-dots">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">// no campaign yet</div>
          <div className="text-zinc-300 mb-6">Trigger the AI orchestrator to generate a randomized, multi-agent campaign blueprint for this thread.</div>
        </div>
      )}

      {/* Edit drawer */}
      {editing && (
        <TaskEditor
          key={editing.id}
          task={editing}
          agents={agents}
          onClose={() => setEditing(null)}
          onSave={(body) => updateTask.mutate({ id: editing.id, body })}
          saving={updateTask.isPending}
          deployed={!!campaign?.deployed_at}
        />
      )}

      {/* Subreddit override modal */}
      {showSubPicker && campaign && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowSubPicker(false)} data-testid="sub-picker-modal">
          <div className="w-full max-w-lg bg-[var(--bg)] border border-white/15" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// campaign · subreddit</div>
                <div className="font-display font-black text-xl tracking-tight">Change target.</div>
              </div>
              <button onClick={() => setShowSubPicker(false)} className="p-1.5 border border-white/10 hover:border-white/30" data-testid="close-sub-picker"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-5">
              <div className="text-[12px] text-zinc-400 leading-relaxed mb-4">
                Currently: <span className="font-mono text-[var(--accent)]">{thread.subreddit}</span><br/>
                Pick a new sub from your saved list. All tasks in this campaign will be re-targeted. Only works before the OP posts.
              </div>
              <div className="max-h-72 overflow-y-auto border border-white/10">
                {(savedSubsQ.data || []).map(s => (
                  <button
                    key={s.id}
                    onClick={() => { overrideSub.mutate(s.name); setShowSubPicker(false); }}
                    disabled={overrideSub.isPending || s.name === thread.subreddit}
                    data-testid={`sub-picker-option-${s.name.replace(/[^a-z0-9]/gi,"-")}`}
                    className="w-full text-left px-4 py-3 hover:bg-white/[0.04] border-b border-white/5 disabled:opacity-40 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-[13px] text-white">{s.name}</div>
                      {s.description && <div className="text-[11px] text-zinc-500 truncate">{s.description}</div>}
                    </div>
                    {s.name === thread.subreddit && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">current</span>}
                  </button>
                ))}
                {(savedSubsQ.data || []).length === 0 && (
                  <div className="p-6 text-center text-[12px] text-zinc-500">
                    No subreddits saved. Add some at <a href="/admin/subreddits" className="text-[var(--accent)] hover:underline">/admin/subreddits</a>.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function TaskEditor({ task, agents, onClose, onSave, saving, deployed }) {
  const [script, setScript] = useState(task.task_script || "");
  const [delay, setDelay] = useState(task.execution_delay_minutes || 0);
  const [agent, setAgent] = useState(task.assigned_agent_id);
  const [action, setAction] = useState(task.action_type);
  const [persona, setPersona] = useState(task.persona_angle);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="task-editor-drawer">
      <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md h-full bg-[#09090B] border-l border-white/10 p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Edit task</div>
            <div className="font-mono text-[11px] text-zinc-400">{task.id.slice(0,8)}</div>
          </div>
          <button onClick={onClose} data-testid="task-editor-close" className="p-1.5 border border-white/10 hover:border-white/30">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Persona</label>
        <select data-testid="edit-persona-select" value={persona} onChange={(e)=>setPersona(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm">
          {PERSONAS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Action</label>
        <select data-testid="edit-action-select" value={action} onChange={(e)=>setAction(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm">
          {ACTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Assign to Agent</label>
        <select data-testid="edit-agent-select" value={agent} onChange={(e)=>setAgent(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm">
          {agents.map(a => <option key={a.id} value={a.id}>{a.name} · {a.email}</option>)}
        </select>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Delay (minutes)</label>
        <input
          type="number" min={0}
          data-testid="edit-delay-input"
          value={delay}
          onChange={(e)=>setDelay(parseInt(e.target.value || "0", 10))}
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm font-mono"
        />
        {deployed && <div className="text-[11px] text-amber-400 mb-4 font-mono">Campaign deployed — changing delay recomputes unlock time.</div>}

        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Task Script</label>
        <textarea
          rows={10}
          data-testid="edit-script-textarea"
          value={script}
          onChange={(e)=>setScript(e.target.value)}
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-[13px] leading-relaxed"
          placeholder="What the agent must post..."
        />

        <button
          onClick={() => onSave({
            task_script: script,
            execution_delay_minutes: delay,
            assigned_agent_id: agent,
            action_type: action,
            persona_angle: persona,
          })}
          disabled={saving}
          data-testid="edit-save-btn"
          className="w-full bg-white text-black font-medium py-2.5 hover:bg-zinc-200 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin"/>}
          SAVE CHANGES
        </button>
      </div>
    </div>
  );
}
